import React from "react";
import {
    Box,
    IconButton,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PieChartIcon from "@mui/icons-material/PieChart";
import { useTranslation } from "shared/hooks/useTranslation";
import type { SecondaryTab } from "shared/hooks/useSecondaryPanelPreference";
import QuotesPanelPane from "features/market/panel/QuotesPanelPane";
import WalletPanelPane from "features/wallet/panel/WalletPanelPane";
import PortfolioPanelPane from "features/investments/panel/PortfolioPanelPane";

export type { SecondaryTab };

interface DesktopSecondaryPanelProps {
    tab: SecondaryTab;
    onTabChange: (tab: SecondaryTab) => void;
    onClose: () => void;
    layout?: "side" | "stacked";
}

const TAB_CONFIG: { value: SecondaryTab; icon: React.ReactElement; labelKey: string }[] = [
    { value: "quotes", icon: <ShowChartIcon fontSize="small" />, labelKey: "navigation.cotizaciones" },
    { value: "wallet", icon: <AccountBalanceWalletIcon fontSize="small" />, labelKey: "navigation.wallet" },
    { value: "portfolio", icon: <PieChartIcon fontSize="small" />, labelKey: "navigation.portfolio" },
];

export default function DesktopSecondaryPanel({
    tab,
    onTabChange,
    onClose,
    layout = "side",
}: DesktopSecondaryPanelProps) {
    const { t } = useTranslation();

    return (
        <Paper
            elevation={8}
            sx={{
                height: layout === "stacked" ? "100%" : "calc(100vh - 32px)",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                bgcolor: "background.paper",
            }}
        >
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    px: 1.5,
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "background.paper",
                }}
            >
                <ToggleButtonGroup
                    value={tab}
                    exclusive
                    onChange={(_e, value: SecondaryTab | null) => value && onTabChange(value)}
                    size="small"
                    sx={{
                        flex: 1,
                        display: "flex",
                        gap: 0.5,
                        "& .MuiToggleButtonGroup-grouped": {
                            flex: 1,
                            border: 0,
                            borderRadius: "999px !important",
                            mx: 0,
                            minHeight: 36,
                            px: 0.75,
                            textTransform: "none",
                            fontWeight: 800,
                            fontSize: 12,
                            gap: 0.5,
                            color: "text.secondary",
                            "&.Mui-selected": {
                                color: "primary.contrastText",
                                bgcolor: "primary.main",
                                "&:hover": { bgcolor: "primary.dark" },
                            },
                            "&:not(.Mui-selected):hover": {
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            },
                        },
                    }}
                >
                    {TAB_CONFIG.map(({ value, icon, labelKey }) => (
                        <ToggleButton
                            key={value}
                            value={value}
                            aria-label={t(labelKey)}
                        >
                            {icon}
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                    fontWeight: "inherit",
                                    display: { xs: "none", md: "inline" },
                                    lineHeight: 1,
                                }}
                            >
                                {t(labelKey)}
                            </Typography>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <Tooltip title={t("common.close")}>
                    <IconButton size="small" onClick={onClose} aria-label={t("common.close")}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box
                sx={{
                    p: 1.5,
                    flex: 1,
                    minHeight: 0,
                    overflow: tab === "wallet" ? "hidden" : "auto",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {tab === "quotes" && <QuotesPanelPane />}
                {tab === "wallet" && <WalletPanelPane />}
                {tab === "portfolio" && <PortfolioPanelPane />}
            </Box>
        </Paper>
    );
}
